"use client";

import { Activity, ArrowUpRight, Dna, HeartPulse, ScanLine, Sparkles, Watch } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Productcategoryfilters() {
  const services = [{
    icon: HeartPulse,
    name: "Surgical Devices",
    description: "Precision-engineered surgical instruments and systems designed to enhance clinical outcomes and procedural efficiency.",
    colSpan: "lg:col-span-5",
    offset: "lg:mt-0"
  }, {
    icon: Dna,
    name: "Diagnostic Devices",
    description: "Advanced diagnostic technologies that provide clinicians with accurate, real-time data for informed decision-making.",
    colSpan: "lg:col-span-6 lg:col-start-7",
    offset: "lg:mt-20"
  }, {
    icon: ScanLine,
    name: "Medical Aesthetics",
    description: "Innovative solutions for dermatological and aesthetic procedures, focusing on safety, efficacy, and patient satisfaction.",
    colSpan: "lg:col-span-6",
    offset: "lg:-mt-8"
  }, {
    icon: Activity,
    name: "Dental Solutions",
    description: "Comprehensive dental equipment and digital tools that support modern practices in delivering superior oral care.",
    colSpan: "lg:col-span-5 lg:col-start-8",
    offset: "lg:mt-12"
  }, {
    icon: Watch,
    name: "AI Healthcare",
    description: "Cutting-edge artificial intelligence platforms that streamline workflows and improve diagnostic accuracy in clinical settings.",
    colSpan: "lg:col-span-5 lg:col-start-2",
    offset: "lg:mt-8"
  }, {
    icon: Sparkles,
    name: "Digital Healthcare",
    description: "Integrated digital health ecosystems that connect patients and providers through seamless data exchange and monitoring.",
    colSpan: "lg:col-span-6",
    offset: "lg:-mt-4"
  }];
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: footerRef,
    isVisible: footerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  return <section data-section-id="4579" id="productcategoryfilters" className="bg-background py-24 md:py-40 text-foreground">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div ref={headerRef} className={`grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-8 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-8">
            <p className="font-default text-xs uppercase tracking-[0.25em] mb-8 text-chart-3">
              — 01 / Our Portfolio
            </p>
            <h2 className="font-default text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-foreground">
              Innovative medical
              <br />
              <em className="italic text-primary">
                technology solutions
              </em>
            </h2>
          </div>
          <div className="lg:col-span-4 lg:pt-6">
            <p className="text-base leading-relaxed text-muted-foreground font-default">
              We curate and distribute premium medical technologies, connecting global manufacturers with healthcare professionals through strategic partnership.
            </p>
          </div>
        </div>

        <div className="mt-20 md:mt-28 grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
          {services.map((service, i) => {
          const Icon = service.icon;
          return <ServiceCard key={i} data-index={i} icon={Icon} name={service.name} description={service.description} colSpan={service.colSpan} offset={service.offset} delay={i * 100} />;
        })}
        </div>

        <div ref={footerRef} className={`mt-24 md:mt-32 text-center transition-all duration-1000 ease-out ${footerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <Link to="/OurProductsPage" className="inline-block font-default italic text-xl md:text-2xl text-foreground hover:opacity-70 transition-opacity duration-500">
            <span className="border-b pb-1 border-chart-3">
              Explore our full product range
            </span>
          </Link>
        </div>
      </div>
    </section>;
}
type ServiceCardProps = {
  icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  name: string;
  description: string;
  colSpan: string;
  offset: string;
  delay: number;
  "data-index": number;
};
function ServiceCard({
  icon: Icon,
  name,
  description,
  colSpan,
  offset,
  delay,
  ...rest
}: ServiceCardProps) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.25
  });
  return <div {...rest} ref={ref} className={`${colSpan} ${offset} group`} style={{
    transitionDelay: `${delay}ms`
  }}>
      <Card className="relative h-full p-8 md:p-10 border bg-card transition-all duration-500 ease-out hover:-translate-y-0.5 shadow-md rounded-lg">
        <div className="absolute inset-0 pointer-events-none transition-colors duration-500 rounded-lg" />
        <style>{`
          .group:hover > [data-card-inner] {
            border-color: var(--chart-3) !important;
          }
        `}</style>
        <div data-card-inner className="absolute inset-0 border transition-colors duration-500 pointer-events-none rounded-lg" style={{
        borderColor: "transparent"
      }} />

        <div className="relative flex flex-col h-full min-h-[260px]">
          <div className="mb-10 transition-transform duration-500 group-hover:rotate-[5deg] text-primary">
            <Icon className="w-7 h-7" strokeWidth={1.25} />
          </div>

          <h3 className={`font-default text-2xl md:text-3xl leading-tight tracking-tight text-card-foreground mb-4 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
          transitionDelay: `${delay + 100}ms`
        }}>
            {name}
          </h3>

          <p className={`text-sm md:text-base leading-relaxed mb-10 transition-all duration-700 ease-out flex-grow text-muted-foreground font-default ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
          transitionDelay: `${delay + 200}ms`
        }}>
            {description}
          </p>

          <div className={`mt-auto transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
          transitionDelay: `${delay + 300}ms`
        }}>
            <Link to="/OurProductsPage" className="inline-flex items-center gap-2 font-default text-xs uppercase tracking-[0.2em] text-foreground hover:opacity-60 transition-opacity duration-300">
              <span>Learn more</span>
              <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </Card>
    </div>;
}